import { useState, useRef, KeyboardEvent } from 'react';
import { Check, X, Mail, Clock, AlertCircle, Send, Ban } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import type { ConnectorDraft, ConnectorDraftPreviewField } from '../../../types/os';

interface Props {
  draft: ConnectorDraft;
  onApprove: (updatedFields: Record<string, unknown>) => void;
  onReject: (reason?: string) => void;
  disabled?: boolean;
  status?: ConnectorDraft['status'];
}

type EditableValue = string | string[];

function asEditable(v: unknown): EditableValue {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  return (v ?? '') as string;
}

function toLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// --- Datetime helpers ---

/**
 * Convert RFC 3339 / ISO string → "YYYY-MM-DDTHH:mm" for datetime-local input.
 * Uses the user's local timezone (Date parses to local time).
 * Note: the round-trip via fromDatetimeLocal emits UTC ISO, which the backend
 * accepts since it handles RFC 3339 with any offset.
 */
function toDatetimeLocal(rfc3339: string | undefined): string {
  if (!rfc3339) return '';
  const d = new Date(rfc3339);
  if (isNaN(d.valueOf())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert "YYYY-MM-DDTHH:mm" from datetime-local → UTC ISO string.
 * The input is interpreted in the user's local timezone by the Date constructor.
 */
function fromDatetimeLocal(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  return d.toISOString();
}

// --- ChipInput sub-component (email_list) ---

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ChipInput({
  value,
  onChange,
  disabled,
  label,
  inputId,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  label: string;
  inputId: string;
}) {
  const [typing, setTyping] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const v = raw.trim().replace(/,+$/, '');
    if (!v) return;
    if (!EMAIL_RE.test(v)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (!value.includes(v)) onChange([...value, v]);
    setTyping('');
  };

  const removeChip = (email: string) => {
    onChange(value.filter((e) => e !== email));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(typing);
    } else if (e.key === 'Backspace' && typing === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    } else {
      // Clear invalid state when the user starts typing again
      if (invalid) setInvalid(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    // Auto-commit if user types a comma
    if (v.endsWith(',')) {
      commit(v);
    } else {
      setTyping(v);
      if (invalid) setInvalid(false);
    }
  };

  const handleBlur = () => {
    if (typing) commit(typing);
  };

  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {/* Chip container — clicking anywhere focuses the input */}
      <div
        className={cn(
          'flex min-h-8 w-full flex-wrap items-center gap-1 rounded-[5px] border bg-card px-2 py-1',
          'focus-within:ring-1 focus-within:ring-primary focus-within:border-primary',
          invalid ? 'border-destructive' : 'border-border-chip',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
        role="group"
        aria-labelledby={inputId}
      >
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 rounded-full border border-border-chip bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground"
          >
            {email}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(email);
                }}
                className="ml-0.5 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                aria-label={`Remove ${email}`}
              >
                <X size={10} />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="email"
          value={typing}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value.length === 0 ? 'email@example.com' : ''}
          className="min-w-[140px] flex-1 bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          aria-label={label}
          aria-invalid={invalid}
        />
      </div>
      {invalid && (
        <p className="text-[11px] text-destructive">Enter a valid email address</p>
      )}
      {!invalid && (
        <p className="text-[11px] text-muted-foreground">Press Enter or comma to add each address</p>
      )}
    </div>
  );
}

// --- Field editors ---

interface FieldEditorProps {
  fieldKey: string;
  field: ConnectorDraftPreviewField;
  value: EditableValue;
  disabled: boolean;
  onChange: (v: EditableValue) => void;
}

function EmailListField({ fieldKey, value, disabled, onChange }: FieldEditorProps) {
  const chips = Array.isArray(value) ? value : value.split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <ChipInput
      inputId={`field-${fieldKey}`}
      label={toLabel(fieldKey)}
      value={chips}
      disabled={disabled}
      onChange={(next) => onChange(next)}
    />
  );
}

function DatetimeField({ fieldKey, value, disabled, onChange }: FieldEditorProps) {
  const inputId = `field-${fieldKey}`;
  const raw = Array.isArray(value) ? value[0] ?? '' : value;
  const localValue = toDatetimeLocal(raw);

  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-xs font-medium text-muted-foreground">
        {toLabel(fieldKey)}
      </label>
      <Input
        id={inputId}
        type="datetime-local"
        value={localValue}
        disabled={disabled}
        onChange={(e) => onChange(fromDatetimeLocal(e.target.value))}
        aria-label={toLabel(fieldKey)}
        className="w-fit"
      />
    </div>
  );
}

function StringField({ fieldKey, value, disabled, onChange }: FieldEditorProps) {
  const inputId = `field-${fieldKey}`;
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-xs font-medium text-muted-foreground">
        {toLabel(fieldKey)}
      </label>
      <Input
        id={inputId}
        value={Array.isArray(value) ? value.join(', ') : value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label={toLabel(fieldKey)}
      />
    </div>
  );
}

function LongTextField({ fieldKey, value, disabled, onChange }: FieldEditorProps) {
  const inputId = `field-${fieldKey}`;
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-xs font-medium text-muted-foreground">
        {toLabel(fieldKey)}
      </label>
      <Textarea
        id={inputId}
        value={Array.isArray(value) ? value.join('\n') : value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[96px] resize-none text-[13px]"
        aria-label={toLabel(fieldKey)}
      />
    </div>
  );
}

function StubField({ fieldKey, value, disabled, onChange }: FieldEditorProps) {
  // Fallback for unknown advanced types (e.g. duration)
  const inputId = `field-${fieldKey}`;
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="block text-xs font-medium text-muted-foreground">
        {toLabel(fieldKey)}
        <span className="ml-1.5 text-[10px] text-muted-foreground/60">(advanced editor coming soon)</span>
      </label>
      <Input
        id={inputId}
        value={Array.isArray(value) ? value.join(', ') : value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label={toLabel(fieldKey)}
      />
    </div>
  );
}

function FieldEditor(props: FieldEditorProps) {
  switch (props.field.type) {
    case 'email_list':
      return <EmailListField {...props} />;
    case 'long_text':
      return <LongTextField {...props} />;
    case 'string':
      return <StringField {...props} />;
    case 'datetime':
      return <DatetimeField {...props} />;
    case 'duration':
      return <StubField {...props} />;
    default:
      return <StringField {...props} />;
  }
}

// --- Status banner ---

function StatusBanner({ status }: { status: NonNullable<ConnectorDraft['status']> }) {
  const config: Record<
    string,
    { icon: React.ReactNode; label: string; className: string }
  > = {
    sent: {
      icon: <Check size={13} />,
      label: 'Sent successfully',
      className: 'bg-status-active/10 text-status-active border-status-active/20',
    },
    rejected: {
      icon: <Ban size={13} />,
      label: 'Rejected',
      className: 'bg-status-cancelled/10 text-status-cancelled border-status-cancelled/20',
    },
    expired: {
      icon: <AlertCircle size={13} />,
      label: 'Expired — this draft can no longer be sent',
      className: 'bg-muted text-muted-foreground border-border',
    },
    sending: {
      icon: <Send size={13} className="animate-pulse" />,
      label: 'Sending…',
      className: 'bg-accent-tint text-foreground border-accent-border',
    },
  };

  const c = config[status];
  if (!c) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium',
        c.className,
      )}
    >
      {c.icon}
      {c.label}
    </div>
  );
}

// --- Provider badge ---

const PROVIDER_LABELS: Record<string, string> = {
  gmail: 'Gmail',
  google_calendar: 'Google Calendar',
  outlook: 'Outlook',
  slack: 'Slack',
  hubspot: 'HubSpot',
};

function providerLabel(p: string) {
  return PROVIDER_LABELS[p] ?? p;
}

// --- Main component ---

export function ConnectorDraftCard({ draft, onApprove, onReject, disabled = false, status }: Props) {
  // Initialise editable state from preview fields
  const initial: Record<string, EditableValue> = {};
  Object.entries(draft.preview.editable_fields).forEach(([k, f]) => {
    initial[k] = asEditable(f.value);
  });
  const [values, setValues] = useState<Record<string, EditableValue>>(initial);

  const setField = (k: string, v: EditableValue) =>
    setValues((prev) => ({ ...prev, [k]: v }));

  const handleApprove = () => onApprove(values);
  const handleReject = () => onReject();

  // Resolved status: prop wins over draft.status
  const resolvedStatus = status ?? draft.status ?? 'pending';
  const isTerminal =
    resolvedStatus === 'sent' || resolvedStatus === 'rejected' || resolvedStatus === 'expired';
  const isBusy = resolvedStatus === 'sending' || disabled;
  const fieldsDisabled = isBusy || isTerminal;
  const buttonsDisabled = isBusy || isTerminal || disabled;

  const hasEditableFields = Object.keys(draft.preview.editable_fields).length > 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border-strong bg-card shadow-card">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 border-b border-border bg-muted/30 px-4 py-3">
        {/* Provider icon area */}
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground shadow-sm">
          <Mail size={15} />
        </div>

        <div className="min-w-0 flex-1">
          {/* Summary */}
          <p className="text-sm font-medium leading-snug text-foreground">
            {draft.preview.summary}
          </p>

          {/* From-account + provider */}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {draft.preview.from_account && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border-chip bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">{draft.preview.from_account}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-border-chip bg-accent-tint px-2 py-0.5 text-[11px] font-medium text-foreground">
              {providerLabel(draft.provider)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Editable fields ─────────────────────────────────────────────── */}
      {hasEditableFields && (
        <div className="space-y-4 px-4 py-4">
          {Object.entries(draft.preview.editable_fields).map(([key, field]) => (
            <FieldEditor
              key={key}
              fieldKey={key}
              field={field}
              value={values[key] ?? asEditable(field.value)}
              disabled={fieldsDisabled}
              onChange={(v) => setField(key, v)}
            />
          ))}
        </div>
      )}

      {/* ── Status banner (non-pending states) ──────────────────────────── */}
      {resolvedStatus !== 'pending' && resolvedStatus !== 'editing' && (
        <div className="px-4 pb-3">
          <StatusBanner status={resolvedStatus} />
        </div>
      )}

      {/* ── Footer: actions ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-4 py-3">
        {resolvedStatus === 'sending' && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock size={12} className="animate-spin" />
            Sending…
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReject}
          disabled={buttonsDisabled}
          className="gap-1.5 text-muted-foreground hover:text-status-cancelled"
          aria-label="Reject"
        >
          <X size={13} />
          Reject
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={handleApprove}
          disabled={buttonsDisabled}
          className="gap-1.5"
          aria-label="Approve & Send"
        >
          <Check size={13} />
          Approve &amp; Send
        </Button>
      </div>
    </div>
  );
}
